import {
  FilterChip,
  Flex,
  MenuCheckboxItem,
  MenuContent,
  MenuItemGroup,
  Text,
} from "shared/components/Spor";
import { Dispatch, FC, SetStateAction, useCallback, useMemo } from "react";
import {
  getSelectedTodoRoleCount,
  getUpdatedRoles,
  TODO_ROLES,
  UserRole,
} from "shared/types/roles";
import { useTranslation } from "react-i18next";
import { VEHICLE_SET_TYPES } from "features/TodoOploggSidebar/formSchema";
import { Dropdown } from "shared/components/forms/Dropdown";

type OploggSendToTodoProps = {
  selectedRoles: UserRole[];
  setSelectedRoles: Dispatch<SetStateAction<UserRole[]>>;
  vehicleSetTypes: string[];
  setVehicleSetTypes: Dispatch<SetStateAction<string[]>>;
};

const ALL_ROLES = TODO_ROLES.reduce<UserRole[]>(
  (selectedRoles, role) => getUpdatedRoles(selectedRoles, role.value),
  [],
);

export const OploggSendToTodo: FC<OploggSendToTodoProps> = ({
  selectedRoles,
  setSelectedRoles,
  vehicleSetTypes,
  setVehicleSetTypes,
}) => {
  const { t } = useTranslation();

  const allRolesSelected = useMemo(
    () =>
      ALL_ROLES.length > 0 &&
      ALL_ROLES.every((role) => selectedRoles.includes(role)),
    [selectedRoles],
  );

  const toggleVehicleSetType = useCallback(
    (value: string) => {
      setVehicleSetTypes((prevTypes) =>
        prevTypes.includes(value)
          ? prevTypes.filter((type) => type !== value)
          : [...prevTypes, value],
      );
    },
    [setVehicleSetTypes],
  );

  return (
    <Flex gap={2}>
      <Dropdown
        label={t("Del med")}
        selectedAmount={getSelectedTodoRoleCount(selectedRoles)}
        flexBasis="170px"
        flexGrow={1}
      >
        <MenuContent maxHeight="400px" overflow="scroll">
          <MenuItemGroup>
            <MenuCheckboxItem
              value="toggle-all-roles"
              checked={allRolesSelected}
              onCheckedChange={() =>
                setSelectedRoles(allRolesSelected ? [] : ALL_ROLES)
              }
            >
              {t("Marker alle")}
            </MenuCheckboxItem>

            {TODO_ROLES.map((role) => (
              <MenuCheckboxItem
                key={role.value}
                value={role.value}
                checked={selectedRoles.includes(role.value)}
                onCheckedChange={() =>
                  setSelectedRoles(getUpdatedRoles(selectedRoles, role.value))
                }
              >
                {role.text}
              </MenuCheckboxItem>
            ))}
          </MenuItemGroup>
        </MenuContent>
      </Dropdown>

      <Dropdown
        label={t("Marker som")}
        selectedAmount={vehicleSetTypes.length}
        positioning={{ placement: "bottom-end" }}
        position="relative"
        flexBasis="164px"
        flexGrow={1}
      >
        <Flex w="194px" gap={1} flexFlow="row wrap">
          {VEHICLE_SET_TYPES.map(({ value, text }) => (
            <FilterChip
              key={value}
              value={value}
              checked={vehicleSetTypes.includes(value)}
              onCheckedChange={() => toggleVehicleSetType(value)}
            >
              <Text whiteSpace="nowrap">
                {text === "Lok og vogn" ? t("Lok og vogn") : text}
              </Text>
            </FilterChip>
          ))}
        </Flex>
      </Dropdown>
    </Flex>
  );
};
